import { useRef } from 'react';
import React from 'react';

import FORM_KEYS from 'apps/web/constants/forms/quizmaker/formKeys';
import useAutosizeTextArea from 'apps/web/hooks/useAutoSizeTextArea';
import { useScrollToError } from 'apps/web/hooks/useScrollToError';

import {
  LC_PAGE_SUBJECT_CHAR_LIMIT,
  LP_PAGE_OBJECTIVE_CHAR_LIMIT,
  TIME_QUIZ_MAKER,
  DIFFICULTY,
} from '@lessonPlanner/constants';
import { Language, AssessmentType } from '@lessonPlanner/enums';

import {
  FileUpload,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
} from '../form-fields';
import { GradeLevelDropdown } from '../Select';

import {
  QUIZMAKER_PAGE_SUBLABEL_ENG,
  QUIZMAKER_PAGE_SUBLABEL_FIL,
} from '../../constants/quizmaker';
import processInput from '../../utils/formInput';

export default function DefaultQuizmaker({
  errors,
  watchedValues,
  setValue,
  triggerValidation,
  clearErrors,
}: {
  errors;
  watchedValues;
  setValue;
  triggerValidation;
  clearErrors;
}) {
  function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(textAreaRef.current, watchedValues.topic);
  useAutosizeTextArea(textAreaRef.current, watchedValues.additionalInformation);

  useScrollToError(errors);

  return (
    <React.Fragment>
      <GradeLevelDropdown
        hasCollege
        error={!!errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        label="Grade Level"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.gradeLevel}
      />
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id="subject"
        label="Subject"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name="subject"
        placeholder={'Input the subject for this quiz'}
        type="subject"
        value={watchedValues.subject}
        isMaxLengthReached={
          watchedValues.subject?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            watchedValues.subject?.trim() || '',
          );
        }}
        onChange={(e) =>
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            e.target.value,
          )
        }
      />
      <TextAreaField
        className="col-span-full"
        error={!!errors.topic}
        errorMessage={!!errors.topic && 'Please enter the Topic'}
        id="topic"
        label="Topic"
        maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
        name="topic"
        placeholder="Input the topic for this quiz"
        rows={2}
        textAreaRef={textAreaRef}
        type="topic"
        value={watchedValues.topic}
        isMaxLengthReached={
          watchedValues.topic?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.topic,
            watchedValues.topic?.trim() || '',
          );
        }}
        onChange={(e) =>
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.topic,
            e.target.value,
          )
        }
      />
      <SelectField
        className="col-span-full"
        error={!!errors.assessmentType}
        errorMessage={errors.assessmentType?.message}
        id="assessmentType"
        label="Assessment Type"
        name="assessmentType"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.assessmentType}
      >
        {Object.values(AssessmentType).map((assessmentType) => (
          <option key={assessmentType} value={assessmentType}>
            {assessmentType}
          </option>
        ))}
      </SelectField>
      <SelectField
        className="col-span-full"
        error={!!errors.time}
        errorMessage={!!errors.time && 'Please select the Time'}
        id="time"
        label="Duration of the Quiz"
        name="time"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.time}
      >
        {TIME_QUIZ_MAKER.map((time, index) => (
          <option key={index} value={time}>
            {time}
          </option>
        ))}
      </SelectField>

      <NumberField
        isWidthFull
        className="col-span-full"
        errorMessage={!!errors.number && 'Please enter the Number of Questions'}
        id="number"
        label="Number of Questions"
        max={20}
        min={1}
        placeholder={'Input the number of questions for this quiz'}
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={Number(watchedValues.number) || 0}
      />

      <SelectField
        className="col-span-full"
        error={!!errors.difficulty}
        id="difficulty"
        label="Difficulty/Complexity"
        name="difficulty"
        value={watchedValues.difficulty}
        errorMessage={
          !!errors.difficulty && 'Please select the Difficulty/Complexity'
        }
        onBlur={() => triggerValidation(FORM_KEYS.difficulty)}
        onChange={(value) => updateFieldValue(FORM_KEYS.difficulty, value)}
      >
        {DIFFICULTY.map((difficulty, index) => (
          <option key={index} value={difficulty}>
            {difficulty}
          </option>
        ))}
      </SelectField>
      <FileUpload
        showSubLabel
        className="col-span-full"
        id="additionalInformation"
        label="Additional Instructional Materials"
        name="additionalInformation"
        setError={setValue}
        clearErrors={(id: string) => {
          clearErrors(id);
        }}
        errorMessage={
          errors.additionalInformation?.message
            ? errors.additionalInformation?.message
            : !!errors.additionalInformation &&
              'Please make sure that you upload a valid file'
        }
        subLabel={
          watchedValues.language === Language.ENGLISH
            ? QUIZMAKER_PAGE_SUBLABEL_ENG
            : QUIZMAKER_PAGE_SUBLABEL_FIL
        }
        onBlur={() => {
          triggerValidation('additionalInformation');
        }}
        onChange={(text: string | null) =>
          updateFieldValue('additionalInformation', text || '')
        }
      />
    </React.Fragment>
  );
}
